import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { paginateQuery } from '@/lib/paginateSupabase';
import { LIDER_IA_COMPONENTS } from '@/data/liderIAEvaluationData';

const RUBRIC_ANSWER_MIN_CHARS = 20;

export interface ParticipantCertStatus {
  user_id: string;
  full_name: string;
  email: string;
  contentPercent: number;
  deliverablesPercent: number;
  projectSubmitted: boolean;
  projectApproved: boolean;
  projectAnswered: boolean;
  labCompleted: boolean;
  meetsDiplomaA: boolean;
  meetsDiplomaB: boolean;
  assignedDiploma: 'A' | 'B' | null;
  meetsAll: boolean; // backward compat: true if any diploma cumple
  missingReasons: string[];
  certificateId: string | null;
  certificateDate: string | null;
  certificateDiplomaType: 'A' | 'B' | null;
  certificateIssuedManually: boolean;
}

export interface ProgramCertificate {
  id: string;
  private_name: string;
  diploma_a_name: string | null;
  diploma_a_priority: number;
  diploma_a_require_content: boolean;
  diploma_a_min_content_percent: number;
  diploma_a_require_deliverables: boolean;
  diploma_a_min_deliverables_percent: number;
  diploma_a_require_project: boolean;
  diploma_a_require_project_approved: boolean;
  diploma_a_require_project_answered: boolean;
  diploma_a_require_lab: boolean;
  diploma_b_name: string | null;
  diploma_b_priority: number;
  diploma_b_require_content: boolean;
  diploma_b_min_content_percent: number;
  diploma_b_require_deliverables: boolean;
  diploma_b_min_deliverables_percent: number;
  diploma_b_require_project: boolean;
  diploma_b_require_project_approved: boolean;
  diploma_b_require_project_answered: boolean;
  diploma_b_require_lab: boolean;
}

function evaluateDiploma(
  cert: ProgramCertificate,
  diploma: 'A' | 'B',
  contentPct: number,
  delivPct: number,
  projectSubmitted: boolean,
  projectApproved: boolean,
  projectAnswered: boolean,
  labCompleted: boolean,
): { meets: boolean; missing: string[]; hasAnyRequirement: boolean } {
  const p = (k: string) => (cert as any)[`diploma_${diploma.toLowerCase()}_${k}`];
  const reqContent = p('require_content');
  const reqDeliv = p('require_deliverables');
  const reqProj = p('require_project');
  const reqProjApproved = p('require_project_approved');
  const reqProjAnswered = p('require_project_answered');
  const reqLab = p('require_lab');
  const minContent = p('min_content_percent') ?? 100;
  const minDeliv = p('min_deliverables_percent') ?? 100;

  const hasAny = reqContent || reqDeliv || reqProj || reqProjApproved || reqProjAnswered || reqLab;
  const missing: string[] = [];
  let meets = hasAny;

  if (reqContent && contentPct < minContent) {
    meets = false;
    missing.push(`Contenido: ${contentPct}% (mín. ${minContent}%)`);
  }
  if (reqDeliv && delivPct < minDeliv) {
    meets = false;
    missing.push(`Entregables: ${delivPct}% (mín. ${minDeliv}%)`);
  }
  if (reqProj && !projectSubmitted) {
    meets = false;
    missing.push('Proyecto no entregado');
  }
  if (reqProjApproved && !projectApproved) {
    meets = false;
    missing.push('Proyecto no aprobado');
  }
  if (reqProjAnswered && !projectAnswered) {
    meets = false;
    missing.push('Preguntas LIDER-IA del proyecto incompletas');
  }
  if (reqLab && !labCompleted) {
    meets = false;
    missing.push('Laboratorio de retos incompleto');
  }
  return { meets, missing, hasAnyRequirement: hasAny };
}

// Orden alfabético A-Z de participantes: criterio principal = primer nombre,
// secundario = apellido (último token). Insensible a mayúsculas/acentos (locale es).
// No modifica datos: es solo orden de presentación.
const NAME_COLLATOR_OPTS: Intl.CollatorOptions = { sensitivity: 'base', numeric: true };
function compareByFirstThenLastName(a: { full_name?: string }, b: { full_name?: string }): number {
  const tokens = (s?: string) => (s || '').trim().split(/\s+/).filter(Boolean);
  const ta = tokens(a.full_name);
  const tb = tokens(b.full_name);
  const firstA = ta[0] || '';
  const firstB = tb[0] || '';
  const lastA = ta.length > 1 ? ta[ta.length - 1] : '';
  const lastB = tb.length > 1 ? tb[tb.length - 1] : '';
  const c1 = firstA.localeCompare(firstB, 'es', NAME_COLLATOR_OPTS);
  if (c1 !== 0) return c1;
  const c2 = lastA.localeCompare(lastB, 'es', NAME_COLLATOR_OPTS);
  if (c2 !== 0) return c2;
  return (a.full_name || '').localeCompare(b.full_name || '', 'es', NAME_COLLATOR_OPTS);
}

export function useCohortCertification(cohortId: string | null, programId: string | null) {
  const queryClient = useQueryClient();

  // Fetch the cohort to know which certificate is selected
  const cohortQuery = useQuery({
    queryKey: ['cert-cohort', cohortId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cohorts')
        .select('id, program_certificate_id')
        .eq('id', cohortId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!cohortId,
  });

  // Fetch all certificates for the program
  const programCertsQuery = useQuery({
    queryKey: ['cert-program-certs', programId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('program_certificates')
        .select('*')
        .eq('program_id', programId!)
        .order('private_name');
      if (error) throw error;
      return (data ?? []) as unknown as ProgramCertificate[];
    },
    enabled: !!programId,
  });

  const selectedCertificateId = cohortQuery.data?.program_certificate_id ?? null;
  const selectedCertificate = (programCertsQuery.data ?? []).find(c => c.id === selectedCertificateId) ?? null;

  const setCohortCertificate = useMutation({
    mutationFn: async (programCertificateId: string | null) => {
      const { error } = await supabase
        .from('cohorts')
        .update({ program_certificate_id: programCertificateId })
        .eq('id', cohortId!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cert-cohort', cohortId] });
    },
  });

  const validateQuery = useQuery({
    queryKey: ['cert-validate', cohortId, programId, selectedCertificateId],
    queryFn: async (): Promise<ParticipantCertStatus[]> => {
      if (!cohortId || !programId || !selectedCertificate) return [];

      const cert = selectedCertificate;

      // Get participants
      const { data: participants, error: pErr } = await supabase
        .from('cohort_participants')
        .select('user_id, profiles:user_id (full_name, email)')
        .eq('cohort_id', cohortId);
      if (pErr) throw pErr;
      if (!participants || participants.length === 0) return [];

      const userIds = participants.map((p: any) => p.user_id);

      // Mandatory content — MISMOS criterios que el dashboard "Progreso por
      // Participante" (CohortProgressTab): dedup por content_id y solo contenidos
      // que existen en content_bank (vía !inner). Sin esto el denominador se
      // inflaba con obligatorios duplicados en varios capítulos u huérfanos sin
      // content_bank, y el % de contenido salía por debajo del real (ej. 49% vs 100%).
      const { data: chaptersRows } = await supabase
        .from('program_chapters')
        .select('id')
        .eq('program_id', programId);
      const chapterIds = (chaptersRows ?? []).map((c: any) => c.id);

      // Snapshot de currículo de la cohorte (congelado al crearla). Si existe, se usa
      // ESE set; si no, se cae al cálculo desde el programa vivo (fallback). Evita que
      // agregar contenido al programa des-certifique retroactivamente cohortes en curso.
      const { data: snapRows } = await supabase
        .from('cohort_required_content')
        .select('content_id')
        .eq('cohort_id', cohortId);
      let mandatoryContentIds: string[] = (snapRows ?? []).map((r: any) => r.content_id);

      if (mandatoryContentIds.length === 0 && chapterIds.length > 0) {
        const { data: cc } = await supabase
          .from('chapter_contents')
          .select('content_id, content_bank!inner(id)')
          .in('chapter_id', chapterIds)
          .eq('is_mandatory', true);
        mandatoryContentIds = Array.from(new Set(
          (cc ?? []).filter((r: any) => r.content_bank).map((r: any) => r.content_id)
        ));
      }

      // Workshops requiring deliverables
      const { data: workshops } = await supabase
        .from('program_workshops')
        .select('id, requires_deliverable')
        .eq('program_id', programId)
        .is('deleted_at', null);

      const workshopsRequiringDeliverable = (workshops ?? [])
        .filter((w: any) => w.requires_deliverable === true)
        .map((w: any) => w.id);
      const totalDeliverables = workshopsRequiringDeliverable.length;

      // Paginar content_progress y participant_deliverables: pueden superar
      // 1000 filas (mandatory_count × userIds y workshops × userIds).
      // CRÍTICO: `.order('id')` para que LIMIT/OFFSET sea determinístico. Sin un
      // ORDER BY estable, Postgres devuelve filas en orden indefinido y entre
      // páginas se pierden/duplican → el conteo de completados queda por debajo
      // del real (ej. 20/41 = 49% en vez de 100%).
      const [contentData, deliverablesData, projectsRes, labRes, certsRes, projectExemptRes, deliverableExemptRes, labExemptRes] = await Promise.all([
        mandatoryContentIds.length > 0
          ? paginateQuery<{ user_id: string; content_id: string; completed: boolean }>((from, to) =>
              supabase.from('content_progress').select('user_id, content_id, completed')
                .in('user_id', userIds)
                .in('content_id', mandatoryContentIds)
                .order('id')
                .range(from, to)
            )
          : Promise.resolve([] as any[]),
        totalDeliverables > 0
          ? paginateQuery<{ user_id: string; workshop_id: string; status: string | null }>((from, to) =>
              supabase.from('participant_deliverables').select('user_id, workshop_id, status')
                .in('user_id', userIds)
                .in('workshop_id', workshopsRequiringDeliverable)
                .order('id')
                .range(from, to)
            )
          : Promise.resolve([] as any[]),
        supabase.from('participant_projects').select('id, user_id, submitted, approved_at, rubric_answers')
          .eq('cohort_id', cohortId),
        supabase.from('challenges').select('user_id, id').in('user_id', userIds),
        supabase.from('certificate_logs').select('id, user_id, generated_at, diploma_type, issued_manually')
          .eq('cohort_id', cohortId)
          .in('user_id', userIds),
        // Exenciones del marcado masivo: separan "cumple el requisito" de
        // "tiene el dato real", para no crear filas falsas en las tablas de arriba.
        (supabase as any).from('project_requirement_exemptions').select('user_id')
          .eq('cohort_id', cohortId)
          .in('user_id', userIds),
        (supabase as any).from('deliverable_requirement_exemptions').select('user_id, workshop_id')
          .eq('cohort_id', cohortId)
          .in('user_id', userIds),
        (supabase as any).from('lab_requirement_exemptions').select('user_id')
          .in('user_id', userIds),
      ]);

      const contentByUser: Record<string, number> = {};
      for (const r of contentData) {
        if (r.completed) contentByUser[r.user_id] = (contentByUser[r.user_id] ?? 0) + 1;
      }

      const deliverablesByUser: Record<string, Set<string>> = {};
      for (const r of deliverablesData) {
        if (r.status === 'submitted' || r.status === 'validated') {
          if (!deliverablesByUser[r.user_id]) deliverablesByUser[r.user_id] = new Set();
          deliverablesByUser[r.user_id].add(r.workshop_id);
        }
      }

      const projectByUser: Record<string, boolean> = {};
      const projectApprovedByUser: Record<string, boolean> = {};
      const projectAnsweredByUser: Record<string, boolean> = {};

      // Helper: marca submitted/approved/answered del proyecto a un usuario
      const markProjectFor = (uid: string, r: any) => {
        if (r.submitted) projectByUser[uid] = true;
        if (r.approved_at) projectApprovedByUser[uid] = true;
        const answers = (r.rubric_answers || {}) as Record<string, string>;
        const completedAnswers = LIDER_IA_COMPONENTS.filter(
          c => (answers[c.id] || '').trim().length >= RUBRIC_ANSWER_MIN_CHARS
        ).length;
        if (completedAnswers >= LIDER_IA_COMPONENTS.length) {
          projectAnsweredByUser[uid] = true;
        }
      };

      // 1) Atribuir a cada owner los flags de su propio proyecto
      const projectsById = new Map<string, any>();
      const projectByOwner = new Map<string, any>();
      for (const r of (projectsRes.data ?? []) as any[]) {
        markProjectFor(r.user_id, r);
        projectsById.set(r.id, r);
        projectByOwner.set(r.user_id, r);
      }

      // 2) Heredar los flags del proyecto del owner a los colaboradores
      //    que también están en la cohorte (project_collaborators)
      const projectIds = Array.from(projectsById.keys());
      if (projectIds.length > 0) {
        const { data: collabRows } = await supabase
          .from('project_collaborators')
          .select('project_id, user_id')
          .in('project_id', projectIds)
          .in('user_id', userIds);
        for (const c of (collabRows ?? []) as any[]) {
          const proj = projectsById.get(c.project_id);
          if (proj) markProjectFor(c.user_id, proj);
        }
      }

      // Lab considered "completed" if user has at least one challenge created
      const challengeIdsByUser: Record<string, string[]> = {};
      for (const r of (labRes.data ?? []) as any[]) {
        if (!challengeIdsByUser[r.user_id]) challengeIdsByUser[r.user_id] = [];
        challengeIdsByUser[r.user_id].push(r.id);
      }
      const allChallengeIds = Object.values(challengeIdsByUser).flat();
      const labByUser: Record<string, boolean> = {};
      if (allChallengeIds.length > 0) {
        const { data: solutions } = await supabase
          .from('challenge_solutions')
          .select('challenge_id')
          .in('challenge_id', allChallengeIds);
        const challengesWithSolution = new Set((solutions ?? []).map((s: any) => s.challenge_id));
        for (const [uid, challengeIds] of Object.entries(challengeIdsByUser)) {
          if (challengeIds.some(cid => challengesWithSolution.has(cid))) labByUser[uid] = true;
        }
      }

      const certByUser: Record<string, { id: string; date: string; type: 'A' | 'B'; manual: boolean }> = {};
      for (const r of (certsRes.data ?? []) as any[]) {
        certByUser[r.user_id] = { id: r.id, date: r.generated_at, type: (r.diploma_type ?? 'A') as 'A' | 'B', manual: !!r.issued_manually };
      }

      // Exenciones: cumplen el requisito sin tener el dato real (marcado masivo).
      const projectExemptSet = new Set((projectExemptRes.data ?? []).map((r: any) => r.user_id));
      const labExemptSet = new Set((labExemptRes.data ?? []).map((r: any) => r.user_id));
      const deliverableExemptByUser: Record<string, Set<string>> = {};
      for (const r of (deliverableExemptRes.data ?? []) as any[]) {
        if (!deliverableExemptByUser[r.user_id]) deliverableExemptByUser[r.user_id] = new Set();
        deliverableExemptByUser[r.user_id].add(r.workshop_id);
      }

      const totalMandatory = mandatoryContentIds.length;

      return participants.map((p: any) => {
        const profile = p.profiles as any;
        const completedContent = contentByUser[p.user_id] ?? 0;
        const contentPercent = totalMandatory > 0 ? Math.round((completedContent / totalMandatory) * 100) : 100;
        const submittedDeliverables = deliverablesByUser[p.user_id]?.size ?? 0;
        // Los talleres eximidos no cuentan contra ESTE usuario en el denominador
        // (no infla "entregables enviados" en otros reportes, solo baja lo exigido).
        const exemptedWorkshops = deliverableExemptByUser[p.user_id]?.size ?? 0;
        const effectiveTotalDeliverables = Math.max(0, totalDeliverables - exemptedWorkshops);
        const deliverablesPercent = totalDeliverables === 0
          ? 100
          : effectiveTotalDeliverables === 0
            ? 100
            : Math.round((submittedDeliverables / effectiveTotalDeliverables) * 100);
        const projectSubmitted = !!projectByUser[p.user_id] || projectExemptSet.has(p.user_id);
        const projectApproved = !!projectApprovedByUser[p.user_id];
        const projectAnswered = !!projectAnsweredByUser[p.user_id];
        const labCompleted = !!labByUser[p.user_id] || labExemptSet.has(p.user_id);

        const evalA = evaluateDiploma(cert, 'A', contentPercent, deliverablesPercent, projectSubmitted, projectApproved, projectAnswered, labCompleted);
        const evalB = evaluateDiploma(cert, 'B', contentPercent, deliverablesPercent, projectSubmitted, projectApproved, projectAnswered, labCompleted);

        const meetsA = evalA.hasAnyRequirement && evalA.meets;
        const meetsB = evalB.hasAnyRequirement && evalB.meets;

        // Pick highest priority among the ones met
        let assigned: 'A' | 'B' | null = null;
        if (meetsA && meetsB) {
          assigned = (cert.diploma_b_priority ?? 2) > (cert.diploma_a_priority ?? 1) ? 'B' : 'A';
        } else if (meetsB) {
          assigned = 'B';
        } else if (meetsA) {
          assigned = 'A';
        }

        const missingReasons: string[] = [];
        if (!meetsA && evalA.hasAnyRequirement) missingReasons.push(`Diploma A (${cert.diploma_a_name ?? 'A'}): ${evalA.missing.join(', ')}`);
        if (!meetsB && evalB.hasAnyRequirement) missingReasons.push(`Diploma B (${cert.diploma_b_name ?? 'B'}): ${evalB.missing.join(', ')}`);

        const cl = certByUser[p.user_id];

        return {
          user_id: p.user_id,
          full_name: profile?.full_name ?? '',
          email: profile?.email ?? '',
          contentPercent,
          deliverablesPercent,
          projectSubmitted,
          projectApproved,
          projectAnswered,
          labCompleted,
          meetsDiplomaA: meetsA,
          meetsDiplomaB: meetsB,
          assignedDiploma: assigned,
          meetsAll: assigned !== null,
          missingReasons,
          certificateId: cl?.id ?? null,
          certificateDate: cl?.date ?? null,
          certificateDiplomaType: cl?.type ?? null,
          certificateIssuedManually: cl?.manual ?? false,
        };
      }).sort(compareByFirstThenLastName);
    },
    enabled: false,
  });

  const generateCertificates = useMutation({
    mutationFn: async (params: {
      userIds: string[];
      cohortId: string;
      programId: string;
      organizationId: string | null;
      participants: ParticipantCertStatus[];
      reissue?: boolean;
    }) => {
      if (!selectedCertificate) throw new Error('No hay certificado seleccionado para esta cohorte');

      const rows = params.participants
        .filter(p => params.userIds.includes(p.user_id) && p.assignedDiploma !== null)
        .map(p => {
          const diploma = p.assignedDiploma!;
          const diplomaName = diploma === 'A'
            ? (selectedCertificate.diploma_a_name ?? 'Diploma A')
            : (selectedCertificate.diploma_b_name ?? 'Diploma B');
          return {
            user_id: p.user_id,
            user_name: p.full_name,
            user_email: p.email,
            certificate_name: diplomaName,
            cohort_id: params.cohortId,
            program_id: params.programId,
            organization_id: params.organizationId,
            program_certificate_id: selectedCertificate.id,
            diploma_type: diploma,
          };
        });

      if (rows.length === 0) return 0;

      // Reemisión: borra los certificados previos para honrar el UNIQUE(user_id, cohort_id)
      if (params.reissue) {
        const { error: delErr } = await supabase
          .from('certificate_logs')
          .delete()
          .eq('cohort_id', params.cohortId)
          .in('user_id', rows.map(r => r.user_id));
        if (delErr) throw delErr;
      }

      const { error } = await supabase.from('certificate_logs').insert(rows);
      if (error) {
        if ((error as any).code === '23505') {
          throw new Error('Algunos participantes ya tienen un certificado emitido. Usa "Reemitir" para reemplazarlo.');
        }
        if ((error as any).code === '42501' || /row-level security/i.test(error.message)) {
          throw new Error('No tienes permisos para emitir certificados en esta organización.');
        }
        throw error;
      }
      return rows.length;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cert-validate', cohortId] });
      queryClient.invalidateQueries({ queryKey: ['cert-config-logs'] });
      queryClient.invalidateQueries({ queryKey: ['generated-certificates'] });
    },
  });

  // Asignación manual (bypassea la validación automática): RPC server-side que
  // valida rol + participación real + resuelve nombre/email desde profiles.
  const issueManualCertificate = useMutation({
    mutationFn: async (params: { userId: string; cohortId: string; diplomaType: 'A' | 'B' | 'NONE'; reason?: string }) => {
      const { data, error } = await (supabase as any).rpc('admin_issue_manual_certificate', {
        _user_id: params.userId,
        _cohort_id: params.cohortId,
        _diploma_type: params.diplomaType,
        _reason: params.reason || null,
      });
      if (error) throw error;
      if (data?.ok === false) throw new Error(data?.error || 'No se pudo asignar el certificado');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cert-validate', cohortId] });
      queryClient.invalidateQueries({ queryKey: ['cert-config-logs'] });
      queryClient.invalidateQueries({ queryKey: ['generated-certificates'] });
    },
  });

  return {
    programCertificates: programCertsQuery.data ?? [],
    selectedCertificate,
    selectedCertificateId,
    setCohortCertificate,
    validationData: validateQuery.data ?? [],
    validationLoading: validateQuery.isFetching,
    runValidation: () => validateQuery.refetch(),
    generateCertificates,
    issueManualCertificate,
  };
}
